import { Box, Stack, Typography } from "@mui/material"

import type { FC, FCWC, ResponsiveImageType } from ".."
import { Container, IconButton, Link, ResponsiveImage, SvgIcon } from ".."
import { LinkType } from "../../graphql/types"

type BrochureCardProps = {
  link: LinkType
  image: ResponsiveImageType
}

type BrochuresCollectionProps = {
  title: string
  count: number
  items: BrochureCardProps[]
}

const BrochuresCollection: FC<BrochuresCollectionProps> = ({ title, count, items }) => {
  return (
    <Container maxWidth={1450} pt={{ xs: 15, md: 30 }} pb={{ xs: 15, md: 45 }}>
      <BrochuresCollectionHeader title={title} count={count} />
      <BrochuresCollectionCardsWrapper>
        {items?.map((item, i) => (
          <BrochureCard key={i} {...item} />
        ))}
      </BrochuresCollectionCardsWrapper>
    </Container>
  )
}

export default BrochuresCollection

/** A sub-component for the brochures collection header */
const BrochuresCollectionHeader: FC<Pick<BrochuresCollectionProps, "title" | "count">> = ({
  title,
  count,
}) => {
  return (
    <Box display="flex" gap={2} mb={{ xs: 6.5, lg: 10 }} px={5}>
      <Typography variant="heading.lg" component="h2">{title}</Typography>
      <Typography variant="body.sm" component={"sup"}>
        {count}
      </Typography>
    </Box>
  )
}

/** A sub-component for the brochures collection cards wrapper */
const BrochuresCollectionCardsWrapper: FCWC = ({ children }) => {
  return (
    <Box
      display="grid"
      gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
      gap={{ xs: 10, md: 20 }}
    >
      {children}
    </Box>
  )
}

/** A sub-component fr the brocures collection individual card */
const BrochureCard: FC<BrochureCardProps> = ({ link, image }) => {
  return (
    <Stack
      direction="column"
      gap={10}
      alignItems="center"
      position="relative"
      sx={{
        "transition": "0.5s",
        "&:hover": {
          "& span": {
            background: (theme) =>
              `linear-gradient(to right, ${theme.palette["primary.800"].main}, ${theme.palette["primary.800"].main})`,
            backgroundSize: "100% 1px",
            backgroundPosition: "0 100%",
            backgroundRepeat: "no-repeat",
          },
          "img": { boxShadow: "1px 1px 12px 0px rgba(0, 0, 0, 0.15)", transform: "scale(1.05)" },
          "button": { opacity: 1, bottom: 20 },
        },
      }}
    >
      <Box position="relative" width="100%" overflow="hidden" borderRadius={1.5}>
        <ResponsiveImage
          image={image}
          borderRadius={1.5}
          sx={{
            transition: (theme) =>
              theme.transitions.create("transform", {
                duration: theme.transitions.duration.standard * 1.5,
              }),
          }}
        />
        <ArrowHoverIcon />
      </Box>
      <Box>
        <Typography
          variant="link.md"
          sx={{
            backgroundSize: "0 1px",
            pb: 1.5,
            transition: "background-size 400ms",
          }}
        >
          {link?.title}
        </Typography>
      </Box>
      <Link type="overlay" href={link?.uri} />
    </Stack>
  )
}

/** A sub-component for the arrow hover icon */
const ArrowHoverIcon: FC = () => {
  return (
    <IconButton
      variant="default"
      icon={<SvgIcon variant="arrowOutward" size={18} />}
      sx={{
        transition: ".3s",
        opacity: 0,
        position: "absolute",
        bottom: 0,
        right: 20,
      }}
    />
  )
}
